import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useTheme } from "../../design-system";
// iOS keyboard is now handled via CSS dvh units and viewport meta tag
import "./ScanLeadModal.css";

const ScanLeadModal = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);
  const [isIOSKeyboardOpen, setIsIOSKeyboardOpen] = useState(false);
  const { theme } = useTheme();

  // Pull-down gesture state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const modalRef = useRef(null);

  const navigate = useNavigate();
  const parentContext = useOutletContext();

  // Track window resize for responsive layout
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Track iOS keyboard state for backdrop-filter fix
  useEffect(() => {
    const checkIOSKeyboard = () => {
      const isOpen = document.documentElement.getAttribute('data-ios-keyboard') === 'open';
      setIsIOSKeyboardOpen(isOpen);
    };

    // Use MutationObserver to watch for attribute changes
    const observer = new MutationObserver(checkIOSKeyboard);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-ios-keyboard']
    });

    // Initial check
    checkIOSKeyboard();

    return () => observer.disconnect();
  }, []);
  
  // Initialize comment text from context (for manual entry)
  const [commentText, setCommentText] = useState(parentContext?.commentedText || "");

  // Sync comment text when context changes
  useEffect(() => {
    if (parentContext?.commentedText !== undefined) {
      setCommentText(parentContext.commentedText);
    }
  }, [parentContext?.commentedText]);

  const handleClose = () => {
    if (isSubmitting || isClosing) return; // Prevent closing during submission or double-trigger
    setIsClosing(true);
    setIsVisible(false);
    navigate(-1);
    parentContext?.closeScanModal?.();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      handleClose();
    }
  };

  // Pull-down gesture handlers
  const handleTouchStart = useCallback((e) => {
    if (isSubmitting) return;
    const touch = e.touches[0];
    const modalRect = modalRef.current?.getBoundingClientRect();
    if (modalRect && touch.clientY - modalRect.top < 60) {
      dragStartY.current = touch.clientY;
      setIsDragging(true);
    }
  }, [isSubmitting]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const deltaY = touch.clientY - dragStartY.current;
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragY > 100) {
      handleClose();
    } else {
      setDragY(0);
    }
  }, [isDragging, dragY]);

  const handleCommentChange = (e) => {
    setCommentText(e.target.value);
    parentContext?.commentBoxChangeHandler?.(e);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // Call the submit handler and wait for it to complete
      // The parent's onSubmit already handles navigation, so we just need to wait
      if (parentContext?.submitClickHandler) {
        await Promise.resolve(parentContext.submitClickHandler());
      }
      // Close modal after submission completes
      // Note: navigate(-1) is already called in onCreate, so we just close the modal
      setIsVisible(false);
      parentContext?.closeScanModal?.();
    } catch (error) {
      console.error("Error submitting:", error);
      setIsSubmitting(false);
    }
  };

  // Get the lead name to display
  const getLeadName = () => {
    const result = parentContext?.result;
    const booking = parentContext?.booking;

    if (result?.n) return result.n;
    if (result?.id && !result.id.includes(",")) return result.id;
    if (result?.email) return result.email;
    if (booking) return booking;
    if (typeof result === "string") return result;
    return "Lead Captured";
  };

  // Check if the value is an email
  const isEmail = (value) => {
    if (!value) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  if (!isVisible) return null;

  const isLight = theme === "light";

  // Inline styles to ensure they apply - theme-aware
  const backdropStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: isLight ? "rgba(0, 0, 0, 0.5)" : "rgba(255, 255, 255, 0.14)",
    // Disable backdrop-filter on iOS when keyboard is open to prevent gray overlay
    backdropFilter: isIOSKeyboardOpen ? "none" : "blur(5.5px)",
    WebkitBackdropFilter: isIOSKeyboardOpen ? "none" : "blur(5.5px)",
    zIndex: 2000,
  };

  const closeButtonStyle = {
    position: "fixed",
    bottom: isDesktop ? "calc(50% + 220px)" : "calc(423px + 16px)",
    left: "50%",
    transform: "translateX(-50%)",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: isLight ? "#FFFFFF" : "#262626",
    border: isLight ? "1px solid #E0E1E6" : "1px solid #404040",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: isLight ? "#0A0A0A" : "#FFFFFF",
    zIndex: 2002,
    padding: 0,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  };

  const modalStyle = {
    position: "fixed",
    ...(isDesktop ? {
      bottom: "50%",
      left: "50%",
      right: "auto",
      transform: "translate(-50%, 50%)",
      width: "100%",
      maxWidth: "390px",
      borderRadius: "20px",
      border: isLight ? "1px solid #E0E1E6" : "1px solid #3A3939",
    } : {
      bottom: 0,
      left: 0,
      right: 0,
      borderRadius: "20px 20px 0 0",
      borderTop: isLight ? "1px solid #E0E1E6" : "1px solid #3A3939",
    }),
    backgroundColor: isLight ? "#ffffff" : "#0A0A0A",
    boxShadow: isLight ? "0px 0px 10px rgba(0, 0, 0, 0.1)" : "0px 0px 10px rgba(255, 255, 255, 0.3)",
    zIndex: 2001,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "16px 30px 40px",
    gap: "30px",
  };

  const dragHandleStyle = {
    width: "32px",
    height: "6px",
    backgroundColor: isLight ? "rgba(0, 0, 0, 0.2)" : "rgba(255, 255, 255, 0.2)",
    borderRadius: "3px",
  };

  const contentStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    width: "100%",
  };

  const successIconStyle = {
    width: "60px",
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#0AD52A",
  };

  const getLeadNameValue = () => {
    const name = getLeadName();
    return name;
  };

  const nameStyle = {
    fontSize: "20px",
    fontWeight: 600,
    color: isLight ? "#0A0A0A" : "#ffffff",
    margin: 0,
    textAlign: "center",
    textTransform: isEmail(getLeadNameValue()) ? "none" : "capitalize",
  };

  const commentSectionStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "4px",
    width: "100%",
  };

  const labelStyle = {
    fontSize: "16px",
    fontWeight: 500,
    color: isLight ? "#0A0A0A" : "#ffffff",
    margin: "0 0 5px 0",
  };

  const textareaStyle = {
    width: "100%",
    height: "113px",
    padding: "11px 12px",
    border: isLight ? "0.8px solid #E0E1E6" : "0.8px solid #3A3939",
    borderRadius: "10px",
    backgroundColor: isLight ? "#ffffff" : "#0A0A0A",
    color: isLight ? "#0A0A0A" : "#ffffff",
    fontSize: "16px", // iOS requires 16px+ to prevent auto-zoom
    fontFamily: "inherit",
    resize: "none",
    outline: "none",
    boxSizing: "border-box",
    WebkitAppearance: "none",
    WebkitUserSelect: "text",
    touchAction: "manipulation",
    WebkitTouchCallout: "none",
  };

  const footerStyle = {
    width: "100%",
  };

  const submitButtonStyle = {
    width: "100%",
    height: "40px",
    backgroundColor: isSubmitting ? "#94a3b8" : "#122891",
    border: `0.5px solid ${isSubmitting ? "#94a3b8" : "#122891"}`,
    color: "#ffffff",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: isSubmitting ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    opacity: isSubmitting ? 0.7 : 1,
    transition: "all 0.15s ease",
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="scan-lead-modal__backdrop"
        style={backdropStyle}
        onClick={handleBackdropClick}
      />

      {/* Close Button (Above Modal) - Hide during submission */}
      {!isSubmitting && (
        <button
          className="scan-lead-modal__close-btn"
          style={{
            ...closeButtonStyle,
            transform: dragY > 0
              ? `translateX(-50%) translateY(${dragY}px)`
              : closeButtonStyle.transform,
            transition: isDragging ? 'none' : 'transform 0.3s ease-out',
          }}
          onClick={handleClose}
          aria-label="Close"
        >
          <Icon icon="iconamoon:close" width="24" height="24" color="currentColor" style={{ display: 'block' }} />
        </button>
      )}

      {/* Modal */}
      <div
        className="scan-lead-modal"
        style={{
          ...modalStyle,
          transform: dragY > 0
            ? (isDesktop ? `translate(-50%, calc(50% + ${dragY}px))` : `translateY(${dragY}px)`)
            : modalStyle.transform,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out',
        }}
        ref={modalRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {isSubmitting ? (
          /* Loading State */
          <div style={{ ...contentStyle, padding: "40px 20px", justifyContent: "center", alignItems: "center" }}>
            <Icon icon="svg-spinners:ring-resize" width="40" height="40" style={{ color: isLight ? "#122891" : "#ffffff", marginBottom: "16px" }} />
            <p style={{ ...labelStyle, textAlign: "center", margin: 0 }}>Saving lead...</p>
          </div>
        ) : (
          <>
            {/* Drag Handle */}
            <div className="scan-lead-modal__drag-handle" style={dragHandleStyle} />

            {/* Content */}
            <div className="scan-lead-modal__content" style={contentStyle}>
              {/* Success Icon */}
              <div className="scan-lead-modal__success-icon" style={successIconStyle}>
                <Icon icon="ep:success-filled" width="60" height="60" />
              </div>

              {/* Lead Name */}
              {!parentContext?.result?.id?.includes(",") && (
                <h2 className="scan-lead-modal__name" style={nameStyle}>{getLeadNameValue()}</h2>
              )}
            </div>

            {/* Comment Section */}
            <div style={commentSectionStyle} className="scan-lead-modal__comment-section">
              <p style={labelStyle} className="scan-lead-modal__comment-label">Add Comments/Notes</p>
              <textarea
                className="scan-lead-modal__textarea"
                style={textareaStyle}
                placeholder="Add comments/notes..."
                value={commentText}
                onChange={handleCommentChange}
                id="scan-lead-comment"
                name="scan-lead-comment"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="sentences"
                spellCheck="false"
                inputMode="text"
                enterKeyHint="done"
                data-1p-ignore="true"
                data-lpignore="true"
                data-form-type="other"
                data-private="true"
                aria-autocomplete="none"
                aria-haspopup="false"
                role="textbox"
              />
            </div>

            {/* Footer */}
            <div className="scan-lead-modal__footer" style={footerStyle}>
              <button
                className="scan-lead-modal__submit-btn"
                style={submitButtonStyle}
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                Save & Continue
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ScanLeadModal;
